// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The machine definition format, as TypeScript. See 04.
//
// A machine definition is **data, never code** (SEC-004, SEC-012). Any player can author one
// and any other player will load it, so nothing here may ever become executable: no
// expression strings to parse, no shader source, no dynamic import, no `eval`. Rules are a
// declarative tree over a closed operator set, interpreted inside a bounded evaluator.
//
// These types are the single description the rest of the format derives from. The published
// JSON Schema at `contracts/pinball/v1/machine/schema.json` is checked against them by a
// test, because two hand-maintained descriptions of the same format drift silently and the
// drift only shows up as a machine that validates on the server and fails in the client.
//
// Every union below is closed on purpose. An unrecognised device kind, collider kind,
// expression operator or action is a **validation error, not a runtime no-op** — a no-op
// would let a definition smuggle in an instruction that one client ignores and another
// honours, which is the same class of bug as a divergent simulation.

import type { MaterialName, MaterialOverride } from '../sim/materials'

// --- primitives --------------------------------------------------------------

/** A playfield-local point in metres. Origin is the playfield centre, +y upfield. */
export type Point2 = readonly [number, number]
/** A point carrying height above the playfield surface, for ramp and wire splines. */
export type Point3 = readonly [number, number, number]

// --- §3 meta -----------------------------------------------------------------

export type Era = 'em-1970s' | 'ss-1980s' | 'dmd-1990s' | 'lcd-modern'
export type AuthorKind = 'first-party' | 'player'
export type ContentRating = 'everyone' | 'teen'

export interface MachineMeta {
  readonly title: string
  readonly subtitle?: string
  readonly era: Era
  readonly author: { readonly kind: AuthorKind; readonly displayName: string }
  readonly difficulty: number
  readonly playersMax: number
  readonly ballsPerGame: number
  readonly estimatedBallTimeSeconds?: number
  readonly contentRating: ContentRating
}

// --- §4 dimensions -----------------------------------------------------------

export interface MachineDimensions {
  readonly playfield: { readonly width: number; readonly length: number }
  readonly inclineDegrees: number
  /**
   * Underside of the glass, metres above the playfield surface.
   *
   * Must exceed the ball's diameter, which sounds too obvious to check and is exactly why it
   * is checked: 04 carried 0.0206 m against a 0.0269875 m ball for a full document revision,
   * and a simulation built to that number puts every ball in permanent contact with the
   * glass while every test still passes.
   */
  readonly glassHeight: number
  /** Vertical cabinet recess, metres; presentation only. Defaults to the original 30 mm. */
  readonly playfieldRecess?: number
  readonly cabinet?: {
    readonly length: number
    readonly width: number
    readonly height: number
  }
  readonly lockdownBarHeight?: number
}

// --- §5 layers ---------------------------------------------------------------

/**
 * A named area of the playfield.
 *
 * Added because the format already *required* one and never defined it: a `gi` lamp must name
 * a region, the validator enforced that it does, and no machine could say what a region was —
 * so `"lowerPlayfield"` was a string that resolved to nothing, any string passed, and general
 * illumination could not be rendered by anything. A required field whose vocabulary the format
 * never declares is a promise with no possible keeper.
 *
 * Rectangles only, and deliberately. General illumination is a bank of bulbs under a section
 * of the playfield; its edges are soft and its shape is "roughly the bottom third". An
 * arbitrary polygon would be a more precise way of describing something that has no precise
 * boundary, and every machine author would then have to draw one.
 */
export interface MachineRegion {
  readonly id: string
  /** Playfield-local, metres. `min` is the bottom-left corner in PH-001's frame. */
  readonly min: Point2
  readonly max: Point2
}

export type LayerKind = 'plane' | 'queue' | 'ramp' | 'wire'

export interface RegionCircle {
  readonly shape: 'circle'
  readonly centre: Point2
  readonly radius: number
}
export interface RegionRect {
  readonly shape: 'rect'
  readonly min: Point2
  readonly max: Point2
}
export type Region = RegionCircle | RegionRect

export interface PlaneLayer {
  readonly id: string
  readonly kind: 'plane'
  readonly z: number
  readonly bounds?: Region
  /** Opt-in raised deck: minimum ball-centre height for entry; underside shots remain below. */
  readonly minEntryZ?: number
}

export interface QueueLayer {
  readonly id: string
  readonly kind: 'queue'
  readonly capacity: number
  readonly z: number
}

export interface RampLayer {
  readonly id: string
  readonly kind: 'ramp'
  readonly spline: readonly Point3[]
  readonly width: number
  readonly wallHeight: number
  readonly entry: { readonly from: string; readonly at: Point2 }
  readonly exit: { readonly to: string; readonly at?: Point2; readonly velocity?: Point2 }
}

export interface WireLayer {
  readonly id: string
  readonly kind: 'wire'
  readonly support?:string
  readonly spline: readonly Point3[]
  readonly captureRadius: number
  readonly exit: { readonly to: string; readonly at?: Point2; readonly velocity?: Point2 }
}

export type MachineLayer = PlaneLayer | QueueLayer | RampLayer | WireLayer

// --- §6 colliders ------------------------------------------------------------

/**
 * A collider the operator menu moves (04 §6, 04 §15, M9-36).
 *
 * **05 §2 has said since M2 that "outlane width is operator-adjustable through post position,
 * exactly as rubber-post placement works on a real machine", and nothing moved a post.** Honouring
 * `outlanePosts` needs the *format* to answer a question 04 §15 does not: which colliders a setting
 * moves, and by how much. This is that answer.
 *
 * **Declared on the collider, not on the setting.** A setting that named geometry would be an
 * operator menu that knows about posts; a post that names a setting is a piece of the machine
 * saying what adjusts it, which is what the coin door actually is. It also keeps the shape data —
 * the offsets are metres in PH-001's frame, next to the coordinates they shift.
 *
 * **Every declared option needs an offset**, checked at load. The failure otherwise is silent and
 * indistinguishable from a working machine: a player picks `hard`, no post moves, and the setting
 * is still hashed into the leaderboard key — so two players on different settings rank separately
 * on a machine that plays identically.
 *
 * The whole collider translates: a polyline moves every point, an arc and a circle move their
 * centre. Nothing rotates and nothing scales, because a post is moved on its own bolt.
 */
export interface ColliderAdjustment {
  /** The operator setting whose value chooses the offset. Must be one this machine declares. */
  readonly setting: string
  readonly axis: 'x' | 'y'
  /** Metres to translate by, one entry per option the setting declares. */
  readonly offsets: { readonly [option: string]: number }
}

export interface SegmentCollider {
  readonly thickness?:number
  readonly id: string
  readonly layer: string
  readonly kind: 'segment'
  readonly a: Point2
  readonly b: Point2
  readonly material: MaterialName
  /** Required when `oneSided`; otherwise the left-hand perpendicular of a → b is used. */
  readonly normal?: Point2
  readonly oneSided?: boolean
  readonly device?: string
  /**
   * Outlane geometry: this surface scatters by `physics.outlaneScatterDegrees` instead of by
   * its material's own angle. See 04 §5 and 02 §7.3 — the outlane is the one place a machine
   * usually wants *more* randomness than the material gives, because a perfectly deterministic
   * outlane is either always fatal or never fatal and both are wrong.
   */
  readonly outlane?: boolean
  /** Moved by an operator setting (04 §15, M9-36). See `ColliderAdjustment`. */
  readonly adjustedBy?: ColliderAdjustment
}

export interface ArcCollider {
  readonly thickness?:number
  readonly id: string
  readonly layer: string
  readonly kind: 'arc'
  readonly centre: Point2
  readonly radius: number
  readonly angleFrom: number
  readonly angleTo: number
  /** `false` = the ball is contained within the circle (a concave corner). See 04 §6. */
  readonly insideOut: boolean
  readonly material: MaterialName
  readonly device?: string
  /**
   * Outlane geometry: this surface scatters by `physics.outlaneScatterDegrees` instead of by
   * its material's own angle. See 04 §5 and 02 §7.3 — the outlane is the one place a machine
   * usually wants *more* randomness than the material gives, because a perfectly deterministic
   * outlane is either always fatal or never fatal and both are wrong.
   */
  readonly outlane?: boolean
  /** Moved by an operator setting (04 §15, M9-36). See `ColliderAdjustment`. */
  readonly adjustedBy?: ColliderAdjustment
}

export interface CircleCollider {
  readonly id: string
  readonly layer: string
  readonly kind: 'circle'
  readonly centre: Point2
  readonly radius: number
  readonly material: MaterialName
  readonly device?: string
  /**
   * Outlane geometry: this surface scatters by `physics.outlaneScatterDegrees` instead of by
   * its material's own angle. See 04 §5 and 02 §7.3 — the outlane is the one place a machine
   * usually wants *more* randomness than the material gives, because a perfectly deterministic
   * outlane is either always fatal or never fatal and both are wrong.
   */
  readonly outlane?: boolean
  /** Moved by an operator setting (04 §15, M9-36). See `ColliderAdjustment`. */
  readonly adjustedBy?: ColliderAdjustment
}

export interface PolylineCollider {
  readonly thickness?:number
  readonly id: string
  readonly layer: string
  readonly kind: 'polyline'
  readonly points: readonly Point2[]
  readonly material: MaterialName
  readonly device?: string
  /**
   * Outlane geometry: this surface scatters by `physics.outlaneScatterDegrees` instead of by
   * its material's own angle. See 04 §5 and 02 §7.3 — the outlane is the one place a machine
   * usually wants *more* randomness than the material gives, because a perfectly deterministic
   * outlane is either always fatal or never fatal and both are wrong.
   */
  readonly outlane?: boolean
  /** Moved by an operator setting (04 §15, M9-36). See `ColliderAdjustment`. */
  readonly adjustedBy?: ColliderAdjustment
}

export type MachineCollider =
  | SegmentCollider
  | ArcCollider
  | CircleCollider
  | PolylineCollider

export const COLLIDER_KINDS = ['segment', 'arc', 'circle', 'polyline'] as const

// --- §7 devices --------------------------------------------------------------

export const DEVICE_KINDS = [
  'flipper',
  'popBumper',
  'slingshot',
  'standupTarget',
  'dropTarget',
  'dropTargetBank',
  'spinner',
  'kicker',
  'magnet',
  'gate',
  'rollover',
  'captiveBall',
  'plunger',
  'autoPlunger',
  'ballSaveGate',
  'diverter',
] as const
export type DeviceKind = (typeof DEVICE_KINDS)[number]

interface DeviceCommon {
  readonly id: string
  readonly kind: DeviceKind
  readonly layer?: string
  readonly coil?: string
  readonly switch?: string
  readonly lamp?: string
}

export interface FlipperDevice extends DeviceCommon {
  readonly kind: 'flipper'
  readonly pivot: Point2
  readonly length: number
  readonly radiusPivot: number
  readonly radiusTip: number
  readonly restAngle: number
  readonly sweep: number
  readonly handedness: 'left' | 'right'
  readonly strength?: number
  /** Return spring torque, N·m. Absolute — see `FLIPPER_RETURN_TORQUE`. */
  readonly returnTorque?: number
  readonly coilRampUp?: number
  readonly eosTorque?: number
  readonly eosAngleDegrees?: number
  readonly material?: MaterialName
}

export interface PopBumperDevice extends DeviceCommon {
  readonly kind: 'popBumper'
  readonly centre: Point2
  readonly skirtRadius: number
  readonly kickSpeed: number
  readonly cooldownMs?: number
}

export interface SlingshotDevice extends DeviceCommon {
  readonly cover?:string
  readonly kind: 'slingshot'
  readonly a: Point2
  readonly b: Point2
  readonly kickSpeed: number
  readonly triggerSpeed?: number
}

export interface TargetDevice extends DeviceCommon {
  readonly kind: 'standupTarget' | 'dropTarget'
  /** A raised rear face blocks the ball without scoring; omitted preserves historical physics. */
  readonly solidBack?: boolean
  readonly a: Point2
  readonly b: Point2
  readonly dropSpeed?: number
}

export interface DropTargetBankDevice extends DeviceCommon {
  readonly kind: 'dropTargetBank'
  readonly targets: readonly string[]
  readonly autoResetMs?: number
}

export interface SpinnerDevice extends DeviceCommon {
  readonly kind: 'spinner'
  readonly a: Point2
  readonly b: Point2
  /**
   * The vane, described the way it can be measured. See 04 §7 — an earlier draft specified a
   * raw moment of inertia two orders of magnitude too large, which nothing about the number
   * itself revealed.
   */
  readonly vaneMass: number
  readonly shaftHeight: number
  readonly vaneDrop: number
  readonly damping: number
}

export interface KickerDevice extends DeviceCommon {
  readonly kind: 'kicker' | 'plunger' | 'autoPlunger'
  readonly centre: Point2
  readonly captureRadius: number
  readonly captureSpeed?: number
  readonly holdMs: number
  readonly exit: { readonly direction: Point2; readonly speed: number; readonly to?: string }
  /** Retain the captured physical ball during lockBall instead of retiring its identity. */
  readonly lockable?: boolean
  readonly rearmMs?: number
}

export interface MagnetDevice extends DeviceCommon {
  readonly kind: 'magnet'
  readonly centre: Point2
  readonly radius: number
  readonly peakForce: number
  /** Pull briefly, then release until every ball leaves. Omitted for authored continuous coils. */
  readonly releaseAfterMs?: number
  readonly falloff: 'inverseSquare' | 'linear'
}

export interface GateDevice extends DeviceCommon {
  readonly mountContacts?:boolean
  readonly kind: 'gate' | 'ballSaveGate' | 'diverter'
  readonly a: Point2
  readonly b: Point2
  readonly passDirection: Point2
  readonly armMass?: number
}

export interface RolloverDevice extends DeviceCommon {
  /** Optional ball-centre height qualification; absent preserves published behavior. */
  readonly zRange?: readonly [number, number]
  readonly kind: 'rollover'
  readonly region: Region
  readonly debounceMs?: number
}

export interface CaptiveBallDevice extends DeviceCommon {
  /** Manufactured housing contacts; absent retains historical replay behavior. */
  readonly cageContacts?: boolean
  readonly kind: 'captiveBall'
  readonly centre: Point2
  readonly travel: number
  readonly direction: Point2
}

export type MachineDevice =
  | FlipperDevice
  | PopBumperDevice
  | SlingshotDevice
  | TargetDevice
  | DropTargetBankDevice
  | SpinnerDevice
  | KickerDevice
  | MagnetDevice
  | GateDevice
  | RolloverDevice
  | CaptiveBallDevice

// --- §8 lamps ----------------------------------------------------------------

export type LampKind = 'insert' | 'gi' | 'flasher'
export type LampShape = 'arrow' | 'round' | 'oval' | 'rect' | 'triangle' | 'star'

export interface MachineLamp {
  readonly id: string
  readonly kind: LampKind
  readonly shape?: LampShape
  readonly position?: Point2
  readonly rotation?: number
  readonly size?: Point2
  readonly radius?: number
  readonly region?: string
  readonly colour: string
  readonly group?: string
}

// --- §9 switches and coils ---------------------------------------------------

export type SwitchKind = 'device' | 'tilt' | 'slamTilt' | 'trough' | 'virtual'

export interface MachineSwitch {
  readonly id: string
  readonly kind?: SwitchKind
  readonly device?: string
  readonly debounceMs?: number
  readonly perRevolution?: boolean
  readonly index?: number
}

export type CoilKind = 'flipper' | 'kicker' | 'bumper' | 'knocker' | 'diverter' | 'magnet' | 'virtual'

export interface MachineCoil {
  readonly id: string
  readonly kind: CoilKind
  readonly pulseMs?: number
}

// --- §10 physics -------------------------------------------------------------

export interface MachinePhysics {
  /** Opt-in recovery semantics; absent preserves published replay behavior. */
  readonly flipperCradleProtection?: boolean
  readonly persistentBallSearch?:boolean
  readonly materials?: Readonly<Partial<Record<MaterialName, MaterialOverride>>>
  readonly ballCount?: number
  readonly ballSpeedClamp?: number
  readonly outlaneScatterDegrees?: number
}

// --- §11 rules ---------------------------------------------------------------

export type VarScope = 'game' | 'player' | 'ball'
export type VarType = 'int' | 'bool' | 'set'

export interface MachineVar {
  readonly id: string
  readonly scope: VarScope
  readonly type: VarType
  readonly initial?: number | boolean
  readonly max?: number
  readonly members?: readonly string[]
}

export interface MachineTimer {
  readonly id: string
  readonly durationMs: number
  readonly pausesDuringMultiball?: boolean
}

/**
 * The complete operator list of 04 §11.3. Closed — anything else is a validation error.
 *
 * Publishing the list and then accepting an eighteenth operator "just this once" is how a
 * sandbox stops being one, so the closure is enforced rather than documented.
 */
export const EXPRESSION_OPERATORS = [
  'eq', 'ne', 'lt', 'lte', 'gt', 'gte',
  'and', 'or', 'not',
  'add', 'sub', 'mul', 'min', 'max',
  'count', 'has', 'var',
  'modeActive', 'timerRunning', 'ballsInPlay', 'playerBall', 'switchClosed', 'lampState',
] as const
export type ExpressionOperator = (typeof EXPRESSION_OPERATORS)[number]

/** An expression node: a literal, or a single-keyed object naming one operator. */
export type Expression = number | boolean | { readonly [key: string]: unknown }

/** The complete action list of 04 §11.4. Closed, for the same reason. */
export const ACTION_NAMES = [
  'award', 'set', 'add', 'lamp', 'lampGroup', 'coil',
  'startTimer', 'stopTimer', 'startMode', 'endMode',
  'startMultiball', 'lockBall', 'enableBallSave',
  'display', 'sound', 'speech', 'knocker', 'awardExtraBall',
] as const
export type ActionName = (typeof ACTION_NAMES)[number]

/**
 * `{"enableBallSave": "operator"}` — the one non-numeric argument the action takes (M9-35, 04 §11.4).
 *
 * 04 §15 gives the operator menu a single `ballSaveSeconds`, and a rulesheet grants several
 * different saves: NEON NOVA grants **five** — 12 s at ball start, 20 s at each of two multiballs,
 * 30 s at another and 60 s in wizard mode. One operator number cannot mean all five, and M9-34
 * proved it by trying: forcing it flattened four, and the C# hundred-game replay family came back
 * with ten different scores and two games that did not score at all.
 *
 * So the *author* says which grant the coin door governs and the rest stay literal. That is what an
 * operator menu is: the rulesheet asks for ball save, and how long is the machine's business.
 *
 * A string rather than an object because there is exactly one thing to say. Anything else in this
 * slot that is not a number is refused at load by `unknown-setting` or `action-malformed`, so a
 * typo cannot become a silent nought-second save.
 */
export const OPERATOR_BALL_SAVE = 'operator'

export type Action = { readonly [key: string]: unknown }

export interface MachineRule {
  readonly id: string
  /** `switch:<id>` · `var:<id>` · `mode:<id>:start|end` · `timer:<id>:expire` · `ball:start|end`. */
  readonly on: string
  readonly when?: Expression
  readonly do: readonly Action[]
}

export interface MachineMode {
  readonly id: string
  readonly title: string
  readonly timer?: string
  readonly shots?: readonly string[]
  readonly onStart?: readonly Action[]
  readonly onShot?: readonly Action[]
  readonly onComplete?: readonly Action[]
  readonly onTimeout?: readonly Action[]
  readonly stackable?: boolean
}

export interface MachineScoring {
  readonly bonus?: {
    readonly base?: readonly { readonly var: string; readonly perUnit: number }[]
    readonly multiplier?: { readonly var: string }
    readonly countUpSound?: string
    readonly heldOnTilt?: boolean
    /**
     * A boolean variable that, when set, carries the bonus multiplier into the next ball.
     *
     * This is the classic Hold Bonus award (05 §5.9). It lives here rather than in a rule
     * because the multiplier is reset by the *ball change*, which no rule can intercept.
     */
    readonly holdVar?: string
  }
  readonly match?: { readonly enabled: boolean; readonly digits: number; readonly awardsCredit?: boolean }
  readonly highScore?: { readonly table: number; readonly initials: number }
  readonly replayScore?: number
}

export interface MachineRules {
  readonly vars?: readonly MachineVar[]
  readonly timers?: readonly MachineTimer[]
  readonly modes?: readonly MachineMode[]
  readonly rules?: readonly MachineRule[]
  readonly scoring?: MachineScoring
}

// --- §12–§15 presentation and operator settings ------------------------------
//
// Structurally validated here and fully specified by their own milestones: the DMD program
// by M3, the art program by M3's wow pass, the sound map by M3's audio pass. They are
// carried as opaque-but-shaped objects rather than `unknown` so that a definition missing
// them fails at load rather than at first render.

export interface MachineDisplay {
  readonly kind: 'dmd' | 'lcd' | 'segment'
  readonly width: number
  readonly height: number
  readonly palette?: string
  readonly scenes: readonly { readonly id: string; readonly [key: string]: unknown }[]
}

/**
 * The complete DMD generator-op list of 04 §12. Closed, like every other vocabulary here.
 *
 * A frame is a list of drawing operations rather than a bitmap, which is what lets a
 * 128 × 32 display cost nothing to store and reproduce identically on two runtimes.
 */
export const DISPLAY_OPS = [
  'text', 'scrollText', 'rect', 'line', 'circle',
  'starfield', 'plasma', 'wipe', 'dissolve', 'invert', 'scoreField',
] as const
export type DisplayOp = (typeof DISPLAY_OPS)[number]

/**
 * The procedural art vocabulary of 04 §13.
 *
 * 04 states that one exists and is versioned but never enumerates it; this is that list,
 * derived from what 05 §7 actually asks a NEON NOVA playfield, backglass and cabinet to
 * draw. Closed for the usual reason and one extra: an art program is executed on a canvas
 * at load, so an unrecognised op is the one place a definition could reach for a renderer
 * capability that only one of the two clients has.
 */
export const ART_OPS = [
  'solid', 'linearGradient', 'radialGradient', 'vignette',
  'starfield', 'rays', 'neonRing', 'glowLine', 'grid', 'chevrons', 'streaks',
  'text', 'arcText', 'laneLetters', 'label', 'wordmark',
  'arrow', 'insert', 'panel', 'border', 'dmdWindow', 'mark',
] as const
export type ArtOp = (typeof ART_OPS)[number]

/**
 * The synthesis vocabulary of 07 §3, as cue `synth` names.
 *
 * Every shipped sound is generated in code, so this list *is* the sound catalogue: a cue
 * naming a synth outside it has no recipe and would play silence, which is the worst
 * possible failure mode because the machine still loads and still scores.
 */
export const SYNTH_NAMES = [
  // Solenoids and mechanisms (07 §3.1, §3.5, §3.6).
  'solenoidThunk', 'popBumper', 'slingshot', 'scoopEject', 'kickback', 'knocker',
  'dropTargetClack', 'bankReset', 'standupTick', 'captiveClick',
  'spinnerClick', 'gateRattle', 'diverter',
  'plungerPull', 'plungerRelease', 'troughKick', 'drain',
  // The ball (07 §3.3).
  'ballRoll', 'ballImpact', 'ballBall',
  // Cabinet and tilt (07 §3.6).
  'cabinetShove', 'tiltBob', 'tiltWarning', 'tiltBuzz',
  // Game tones (07 §3.2).
  'chime', 'brassStab', 'sweepUp', 'sweepDown', 'blip', 'arpRun', 'noiseBurst', 'subDrop',
] as const
export type SynthName = (typeof SYNTH_NAMES)[number]

export interface MachineArt {
  readonly seed: number
  readonly playfield?: Record<string, unknown>
  readonly backglass?: Record<string, unknown>
  readonly cabinet?: Record<string, unknown>
  readonly plastics?: Record<string, unknown>
}

/**
 * The machine's voice (07 §4).
 *
 * `kind` is `'formant'` and the validator allows nothing else, because a recorded or
 * text-to-speech voice would break parity between the two cores and bring back the asset
 * licensing this product exists without. `profile` names one of the synthesizer's voices and
 * `pitch` scales its base frequency.
 */
export interface MachineVoice {
  readonly kind: string
  readonly profile: string
  readonly pitch?: number
}

/**
 * One callout.
 *
 * **Authored as phonemes, not as text.** 07 §4: doing it this way "keeps quality under the
 * designer's control and removes any dependence on a text-to-speech engine, which would differ
 * between browsers and Unity and break parity". `text` is not the input to the synthesizer —
 * it is the caption NFR-022 requires, and it is required even for a line with no phonemes yet.
 *
 * `emphasis` marks stressed **syllables** by index, which is the unit a listener hears stress
 * on. The synthesizer divides the phoneme string into syllables itself.
 */
export interface MachineSpeechLine {
  readonly text: string
  readonly phonemes?: string
  readonly emphasis?: readonly number[]
}

/**
 * The machine's music (07 §5).
 *
 * `kind` is `'sequencer'` and the validator allows nothing else — a rendered track would be an
 * asset, and this product ships none. `program` names a score the client knows how to play.
 */
export interface MachineMusic {
  readonly kind: string
  readonly program: string
}

export interface MachineAudio {
  readonly voice?: MachineVoice
  readonly music?: MachineMusic
  readonly cues?: Record<string, Record<string, unknown>>
  readonly speech?: Record<string, MachineSpeechLine>
}

export interface MachineSettingNumber {
  readonly default: number
  readonly range?: readonly [number, number]
}
export interface MachineSettingBoolean {
  readonly default: boolean
}
export interface MachineSettingChoice {
  readonly default: string
  readonly options: readonly string[]
}
export type MachineSetting = MachineSettingNumber | MachineSettingBoolean | MachineSettingChoice

// --- the document ------------------------------------------------------------

/**
 * One appearance of a toy, and the lowest driver value that selects it (04, 05 §3).
 *
 * A toy is a **state display**, not decoration: "every great 1990s machine has one thing on the
 * playfield that moves and that you look at", and what makes it worth looking at is that it is
 * telling you something. So an appearance is declared against a *value*, and the machine's rules
 * move the value — the renderer never decides what the toy should look like.
 */
export interface ToyStage {
  /** The lowest driver value this appearance covers. Stages are declared in ascending order. */
  readonly from: number
  /** `#RRGGBB`. */
  readonly colour: string
  /**
   * Breaths per second. Zero is a steady light.
   *
   * **Capped below 3 Hz by the validator** (NFR-026): nothing in this product may flash between
   * 3 Hz and 55 Hz at high contrast, and a toy is the brightest thing on the playfield. A
   * machine that asks for a faster pulse is refused rather than quietly slowed, because a
   * silently altered rate is a rate the author will keep re-typing.
   */
  readonly pulseHz: number
  /** How many corona rings stand off the dome, 0…3. */
  readonly coronaRings?: number
  /** How unstable the light is, 0…1. Rendered as slow drift, never as a strobe. */
  readonly flicker?: number
  /** Rings travel inward rather than outward — a star falling in rather than burning out. */
  readonly collapsing?: boolean
}

/**
 * A lit dome at the centre of the playfield, showing a rule variable (05 §3).
 *
 * Emissive geometry with no dynamic light of its own (ARCH-011): sixty flashing inserts cost one
 * lamp-state upload rather than sixty lights, and the same argument applies here with more force,
 * because the toy is on screen for the whole game.
 */
export interface DomeToy {
  readonly id: string
  readonly kind: 'dome' | 'hull' | 'portal'
  readonly layer: string
  /** Playfield-local centre, metres. */
  readonly centre: readonly [number, number]
  readonly radius: number
  /** How far the dome stands off the wood, metres. */
  readonly height: number
  /** The rule variable whose value picks the stage. */
  readonly chargeVar: string
  readonly stages: readonly ToyStage[]
  /**
   * An optional collapse: a counter, and how small the dome gets at the last stage.
   *
   * 05 §3's SUPERNOVA — "collapses visibly one stage per 5 shots, dome shrinking and
   * brightening". Size is the cue that survives being colour-blind, being on a phone at arm's
   * length, and having the machine at the far end of an arcade.
   */
  readonly collapse?: {
    readonly var: string
    readonly stages: number
    /** Radius at the last stage, as a fraction of `radius`. */
    readonly shrinkTo: number
  }
  /**
   * An optional climax: while this mode runs, the dome whites out and the corona blows outward.
   *
   * 05 §3's BIG BANG. A single transition rather than a repeated flash, and one a player who has
   * asked for reduced motion gets as a fade.
   */
  readonly climax?: { readonly mode: string; readonly holdMs: number }
}

/** Everything a machine can put on the playfield that is a state display rather than a part. */
export type MachineToy = DomeToy

export interface MachineDefinition {
  readonly formatVersion: string
  readonly machineId: string
  readonly machineVersion: string
  readonly meta: MachineMeta
  readonly dimensions: MachineDimensions
  readonly layers: readonly MachineLayer[]
  /** Named areas, for general illumination and anything else that lights a *part* of the wood. */
  readonly regions?: readonly MachineRegion[]
  readonly colliders: readonly MachineCollider[]
  readonly devices: readonly MachineDevice[]
  readonly lamps?: readonly MachineLamp[]
  readonly switches?: readonly MachineSwitch[]
  readonly coils?: readonly MachineCoil[]
  /** Playfield toys — the one thing that moves and that you look at (05 §3). */
  readonly toys?: readonly MachineToy[]
  readonly physics?: MachinePhysics
  readonly rules?: MachineRules
  readonly display?: MachineDisplay
  readonly art?: MachineArt
  readonly audio?: MachineAudio
  readonly settings?: Readonly<Record<string, MachineSetting>>
}

/**
 * The format version this build reads. Additive optional fields are a minor bump (04 §17).
 *
 * **2.0 on 19 Aug 2026**, because a flipper's `returnRatio` — the return spring as a fraction of
 * the coil — became `returnTorque` in newton-metres. A rename is not additive, so it is a major
 * bump, and the reason it is worth one is in `FLIPPER_RETURN_TORQUE`: the fraction form made
 * retuning a coil silently retune its return spring. It rides along with the `CORE_VERSION` bump
 * the contact-solver fix already forced, so exactly one republish covers both.
 */
export const FORMAT_VERSION = '2.0'
